import React, { createContext, useState, useCallback, useContext, useEffect } from 'react';
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
} from 'amazon-cognito-identity-js';
import { userPool } from '../config/cognitoConfig';

const STORAGE_KEY = 'clarity2work-auth';
const USERNAME_EMAIL_MAP_KEY = 'clarity2work-username-email-map';

export const AuthContext = createContext({
  isLoggedIn: false,
  user: null,
  signup: function () {},
  signin: function () {},
  confirmSignUp: function () {},
  resendConfirmationCode: function () {},
  login: function () {},
  logout: function () {},
  getSessionIdToken: function () {},
  loading: false,
});

/** Returns a Promise that resolves to the current Cognito ID token JWT string, or null if not logged in / session invalid. */
export function getSessionIdToken() {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }
    cognitoUser.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }
      try {
        resolve(session.getIdToken().getJwtToken());
      } catch {
        resolve(null);
      }
    });
  });
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

function getCurrentUser() {
  return new Promise((resolve) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session || !session.isValid()) {
        resolve(null);
        return;
      }

      cognitoUser.getUserAttributes((err, attributes) => {
        if (err) {
          resolve(null);
          return;
        }

        const userData = {};
        attributes.forEach((attr) => {
          userData[attr.Name] = attr.Value;
        });

        // Get username from the CognitoUser object
        const username = cognitoUser.getUsername();

        resolve({
          email: userData.email,
          username: username,
          phone_number: userData.phone_number || null,
        });
      });
    });
  });
}

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    isLoggedIn: false,
    user: null,
    loading: true,
  });

  useEffect(() => {
    // Check for existing session on mount
    getCurrentUser()
      .then((user) => {
        if (user) {
          setState({
            isLoggedIn: true,
            user,
            loading: false,
          });
        } else {
          setState({
            isLoggedIn: false,
            user: null,
            loading: false,
          });
        }
      })
      .catch(() => {
        setState({
          isLoggedIn: false,
          user: null,
          loading: false,
        });
      });
  }, []);

  const signup = useCallback(function (userName, email, mobileNumber, password) {
    return new Promise((resolve, reject) => {
      // Username is now the sign-in identifier, email is a required attribute
      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: email,
        }),
      ];

      // Add phone_number if provided (Cognito standard attribute; E.164 format preferred)
      if (mobileNumber && typeof mobileNumber === 'string' && mobileNumber.trim()) {
        const normalized = mobileNumber.trim().startsWith('+') ? mobileNumber.trim() : '+' + mobileNumber.trim().replace(/\D/g, '');
        attributeList.push(
          new CognitoUserAttribute({
            Name: 'phone_number',
            Value: normalized,
          })
        );
      }

      // Use the username entered by the user as the Cognito username (sign-in identifier)
      userPool.signUp(userName, password, attributeList, null, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        
        resolve(result);
      });
    });
  }, []);

  const confirmSignUp = useCallback(function (username, code) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, (err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }, []);

  const resendConfirmationCode = useCallback(function (username) {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.resendConfirmationCode((err, result) => {
        if (err) {
          reject(err);
          return;
        }
        resolve(result);
      });
    });
  }, []);

  const signin = useCallback(function (username, password) {
    return new Promise((resolve, reject) => {
      // Username is now the sign-in identifier, so we can use it directly
      const authenticationDetails = new AuthenticationDetails({
        Username: username,
        Password: password,
      });

      const cognitoUser = new CognitoUser({
        Username: username,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          cognitoUser.getUserAttributes((err, attributes) => {
            if (err) {
              reject(err);
              return;
            }

            const userData = {};
            attributes.forEach((attr) => {
              userData[attr.Name] = attr.Value;
            });

            const user = {
              email: userData.email,
              username: username, // Username is the sign-in identifier
              phone_number: userData.phone_number || null,
            };

            setState({
              isLoggedIn: true,
              user,
              loading: false,
            });

            resolve({ user, session: result });
          });
        },
        onFailure: (err) => {
          reject(err);
        },
      });
    });
  }, []);

  const logout = useCallback(function () {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }
    setState({
      isLoggedIn: false,
      user: null,
      loading: false,
    });
  }, []);

  // Backward compatibility method for OTPVerification
  // Note: This doesn't create a Cognito session, use signin for proper authentication
  const login = useCallback(function (email) {
    setState({
      isLoggedIn: true,
      user: { email: email || '' },
      loading: false,
    });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, signup, signin, confirmSignUp, resendConfirmationCode, login, logout, getSessionIdToken }}>
      {children}
    </AuthContext.Provider>
  );
}
